import { cloneElement, isValidElement, useId, type ReactElement, type ReactNode } from 'react';
import { cn } from '@/lib/cn';

interface FieldProps {
  label: ReactNode;
  /** The control. Field wires up id, aria-invalid and aria-describedby on it. */
  children: ReactElement<Record<string, unknown>>;
  /** A react-hook-form error (anything with `message`) or a plain string. */
  error?: { message?: string } | string;
  hint?: ReactNode;
  required?: boolean;
  className?: string;
}

/** Label + control + hint/error, accessible by default. */
export function Field({ label, children, error, hint, required, className }: FieldProps) {
  const id = useId();
  const message = typeof error === 'string' ? error : error?.message;
  const describedBy = message ? `${id}-error` : hint ? `${id}-hint` : undefined;
  const control = isValidElement(children)
    ? cloneElement(children, { id: (children.props.id as string) ?? id, 'aria-invalid': message ? true : undefined, 'aria-describedby': describedBy })
    : children;
  return (
    <div className={cn('flex flex-col gap-1', className)}>
      <label htmlFor={(isValidElement(children) && (children.props.id as string)) || id} className="text-xs font-medium text-muted">
        {label}
        {required ? <span className="text-danger"> *</span> : null}
      </label>
      {control}
      {message ? (
        <p id={`${id}-error`} className="text-xs text-danger">
          {message}
        </p>
      ) : hint ? (
        <p id={`${id}-hint`} className="text-xs text-subtle">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

/** Two-column responsive grid for forms. */
export const FormGrid = ({ children, className }: { children: ReactNode; className?: string }) => (
  <div className={cn('grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-2', className)}>{children}</div>
);
